import React, { useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import { IReportBuilderInfoProps } from '../types';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import RetentionBuilderContent from './RetentionBuilderContent';
import DataTableContent from './DataTableContent';
import FunnelBuilderContent from './FunnelBuilderContent';
import * as BuilderInfoUI from '../BuilderInfoUI';
import {
  BuilderMeasureInfo,
  InfoPopupBreakdown,
  InfoPopupFooter,
  InfoPopupGlobalFilter,
  InfoPopupHeader,
  InfoPopupSubHeader,
} from '../components';
import getMeasureFiltersInfo from './utils/getMeasureFiltersInfo';
import { assertCondition, assertNotNil } from '../../../utils/types';
import useAnalysisBuilderInfo from '../../../redux/selectorHooks/useAnalysisBuilderInfo';
import { datePickerFromAnalysisInfo } from '../../Builder/DatePickerWidget/utils';

export default ({
  analysisId,
  onShowInReport,
  chartData,
}: IReportBuilderInfoProps) => {
  const analysisBuilderInfo = useAnalysisBuilderInfo(analysisId);
  assertNotNil(analysisBuilderInfo);

  const {
    query: builderInfo,
    display,
    executedAt,
    description,
    queryType,
    creator,
  } = analysisBuilderInfo;

  assertCondition(
    // Prediction doesn't have a Report info
    builderInfo.__typename !== 'PredictionQuery' &&
      // Cohort has a separate component
      builderInfo.__typename !== 'CohortSampleQuery' &&
      // Path has a separate component
      builderInfo.__typename !== 'PathQuery' &&
      // Significance has a separate component
      builderInfo.__typename !== 'SignificanceQuery',
  );

  const breakdown = builderInfo.breakdown;

  const datePicker = useMemo(
    () => datePickerFromAnalysisInfo(analysisBuilderInfo),
    [analysisBuilderInfo],
  );

  const content = useMemo(() => {
    if (queryType === QueryType.Retention) {
      assertCondition(builderInfo.__typename === 'RetentionQuery');

      return (
        <RetentionBuilderContent query={builderInfo} datePicker={datePicker} />
      );
    }

    if (queryType === QueryType.Expression) {
      assertCondition(builderInfo.__typename === 'ExpressionQuery');
      const { measure, measureProperties } = builderInfo;

      return (
        <BuilderMeasureInfo
          measure={measure}
          measureProperties={measureProperties}
          showLabels
        />
      );
    }

    if (queryType === QueryType.DataTable) {
      assertCondition(builderInfo.__typename === 'DataTableQuery');
      const { measures, measuresProperties } = builderInfo;

      return (
        <DataTableContent
          measures={measures}
          measuresProperties={measuresProperties}
        />
      );
    }

    if (queryType === QueryType.Funnel) {
      assertCondition(builderInfo.__typename === 'FunnelQuery');

      return <FunnelBuilderContent query={builderInfo} />;
    }

    return null;
  }, [builderInfo, datePicker, queryType]);

  const globalCohorts =
    'cohortRefs' in builderInfo ? builderInfo.cohortRefs : [];

  const measureFilterValues = useMemo(() => {
    if (queryType === QueryType.Expression) {
      assertCondition(builderInfo.__typename === 'ExpressionQuery');
      const { measure, measureProperties } = builderInfo;

      return getMeasureFiltersInfo({
        queryType,
        measure,
        measureProperties,
      });
    }

    if (queryType === QueryType.DataTable) {
      assertCondition(builderInfo.__typename === 'DataTableQuery');
      const { measuresProperties } = builderInfo;

      return getMeasureFiltersInfo({
        queryType,
        measuresProperties,
      });
    }

    return [];
  }, [builderInfo, queryType]);

  return (
    <>
      <InfoPopupHeader
        name={display}
        description={description}
        creator={creator}
        createdAt={executedAt}
      />
      <BuilderInfoUI.MainContent>
        <InfoPopupSubHeader analysisId={analysisId} />
        {content}
        {/* Global filter */}
        {(!isEmpty(builderInfo?.filter) ||
          measureFilterValues.length > 0 ||
          globalCohorts.length > 0) && (
          <InfoPopupGlobalFilter
            filter={builderInfo?.filter}
            measureFilterValues={measureFilterValues}
            globalCohorts={globalCohorts}
          />
        )}
        {/* Breakdown */}
        {breakdown && (
          <InfoPopupBreakdown breakdown={breakdown} queryType={queryType} />
        )}
      </BuilderInfoUI.MainContent>
      <InfoPopupFooter
        analysisId={analysisId}
        onShowInReport={onShowInReport}
        chartData={chartData}
      />
    </>
  );
};
